import {
  ArrowLeft,
  Ban,
  CalendarClock,
  Check,
  CircleCheck,
  Copy,
  Download,
  Eye,
  Film,
  Inbox,
  KeyRound,
  Link2,
  LogIn,
  LogOut,
  MonitorPlay,
  PhoneOff,
  Radio,
  RefreshCw,
  ShieldAlert,
  TriangleAlert,
  Users,
  Video,
  VideoOff,
  MessageSquare,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  AdminMeetingLinksResponse,
  MeetingEventType,
  MeetingEventView,
  MeetingParticipantView,
  MeetingRecordingView,
  MeetingRole,
  MeetingView,
} from '@hiredude/meet-shared';
import { joinWindowState } from '@hiredude/meet-shared';

import {
  errorMessage,
  isNotFound,
  isSessionExpired,
  REQUEST_TIMEOUT_MS,
  withTimeout,
} from '@/components/admin/adminError';
import {
  EM_DASH,
  formatBytes,
  formatDateTime,
  formatDurationMs,
  formatMinutes,
  formatWindow,
} from '@/components/admin/format';
import { JoinWindowSummary } from '@/components/admin/joinWindow';
import {
  JoinLinkStatusBadge,
  MeetingStatusBadge,
  RecordingModeLabel,
  RecordingStatusBadge,
  type BadgeVariant,
} from '@/components/admin/status';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import {
  adminCancelMeeting,
  adminMeeting,
  adminMeetingChat,
  adminMeetingEvents,
  adminMeetingLinks,
  adminMeetingRecordings,
  type AdminChatMessage,
} from '@/lib/adminApi';
import { cn, initials } from '@/lib/utils';

interface DetailData {
  meeting: MeetingView;
  events: MeetingEventView[];
  recordings: MeetingRecordingView[];
  messages: AdminChatMessage[];
}

const TONE_RING: Record<BadgeVariant, string> = {
  default: 'border-primary/40 bg-primary/15 text-primary-foreground',
  outline: 'border-border bg-white/[0.04] text-muted-foreground',
  success:
    'border-[color-mix(in_oklab,var(--success)_35%,transparent)] bg-[color-mix(in_oklab,var(--success)_14%,transparent)] text-[var(--success)]',
  warning:
    'border-[color-mix(in_oklab,var(--warning)_35%,transparent)] bg-[color-mix(in_oklab,var(--warning)_14%,transparent)] text-[var(--warning)]',
  destructive:
    'border-[color-mix(in_oklab,var(--destructive)_40%,transparent)] bg-[color-mix(in_oklab,var(--destructive)_16%,transparent)] text-[color-mix(in_oklab,var(--destructive)_80%,white)]',
};

const EVENT_META: Record<MeetingEventType, { icon: LucideIcon; tone: BadgeVariant }> = {
  'meeting.created': { icon: CalendarClock, tone: 'outline' },
  'meeting.started': { icon: Radio, tone: 'success' },
  'meeting.reopened': { icon: RefreshCw, tone: 'warning' },
  'meeting.ended': { icon: CircleCheck, tone: 'outline' },
  'meeting.cancelled': { icon: Ban, tone: 'destructive' },
  'participant.joined': { icon: LogIn, tone: 'success' },
  'participant.left': { icon: LogOut, tone: 'outline' },
  'track.published': { icon: MonitorPlay, tone: 'default' },
  'recording.started': { icon: Video, tone: 'warning' },
  'recording.completed': { icon: CircleCheck, tone: 'success' },
  'recording.failed': { icon: TriangleAlert, tone: 'destructive' },
};

function detailString(detail: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = detail?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

function eventSentence(event: MeetingEventView): string {
  const who = event.name ?? event.identity ?? 'Someone';

  switch (event.type) {
    case 'meeting.created':
      return 'Meeting created';
    case 'meeting.started':
      return 'Meeting started';
    case 'meeting.reopened':
      return 'Meeting reopened for another session';
    case 'meeting.ended':
      return 'Meeting ended';
    case 'meeting.cancelled':
      return 'Meeting cancelled';
    case 'participant.joined':
      return `${who} joined`;
    case 'participant.left':
      return `${who} left`;
    case 'track.published': {
      const source = detailString(event.detail, 'source');
      return source ? `${who} started sending ${source}` : `${who} started sending media`;
    }
    case 'recording.started':
      return 'Recording started';
    case 'recording.completed':
      return 'Recording completed';
    case 'recording.failed':
      return 'Recording failed';
    default:
      return 'Activity recorded';
  }
}

const ROLE_VARIANT: Record<MeetingRole, BadgeVariant> = {
  host: 'default',
  guest: 'outline',
};

export function AdminMeetingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<DetailData | null>(null);
  const [error, setError] = useState<{ message: string; notFound: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setError({ message: 'No meeting was requested.', notFound: true });
      return;
    }

    let ignore = false;
    setLoading(true);

    withTimeout(
      Promise.all([
        adminMeeting(id),
        adminMeetingEvents(id),
        adminMeetingRecordings(id),
        adminMeetingChat(id),
      ]),
      REQUEST_TIMEOUT_MS,
      'The server took too long to respond.',
    )
      .then(([meeting, events, recordings, chat]) => {
        if (ignore) return;
        setData({
          meeting,
          events: events.events,
          recordings: recordings.recordings,
          messages: chat.messages,
        });
        setError(null);
      })
      .catch((err: unknown) => {
        if (ignore) return;
        if (isSessionExpired(err)) {
          navigate('/admin/login', { replace: true });
          return;
        }
        setError({
          message: errorMessage(err, 'Could not load this meeting.'),
          notFound: isNotFound(err),
        });
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [id, reloadToken, navigate]);

  const timeline = useMemo(
    () =>
      [...(data?.events ?? [])].sort(
        (left, right) => new Date(left.at).getTime() - new Date(right.at).getTime(),
      ),
    [data],
  );

  if (loading && !data) {
    return <DetailSkeleton />;
  }

  if (!data) {
    return (
      <div className="mx-auto w-full max-w-3xl p-4 sm:p-6 lg:p-8">
        <BackLink />
        <Card
          className={cn(
            'mt-4 p-8 text-center',
            error?.notFound
              ? 'border-border/70'
              : 'border-[color-mix(in_oklab,var(--destructive)_40%,transparent)]',
          )}
        >
          <span
            className={cn(
              'mx-auto grid size-12 place-items-center rounded-2xl border',
              error?.notFound ? TONE_RING.outline : TONE_RING.destructive,
            )}
          >
            {error?.notFound ? (
              <Inbox className="size-5" aria-hidden />
            ) : (
              <TriangleAlert className="size-5" aria-hidden />
            )}
          </span>

          <h1 className="mt-5 text-xl font-semibold tracking-tight text-balance">
            {error?.notFound ? 'Meeting not found' : 'Could not load this meeting'}
          </h1>
          <p className="text-muted-foreground mx-auto mt-2 max-w-sm text-sm text-pretty" role="alert">
            {error?.notFound
              ? 'This meeting may have been removed, or the link you followed is wrong.'
              : (error?.message ?? 'Something went wrong.')}
          </p>

          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Button variant="outline" asChild>
              <Link to="/admin/meetings">
                <ArrowLeft className="size-4" />
                Back to meetings
              </Link>
            </Button>
            {!error?.notFound && (
              <Button variant="outline" onClick={reload}>
                <RefreshCw className="size-4" />
                Retry
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  }

  const { meeting, recordings, messages } = data;
  const externalRef = meeting.externalRef;
  const refFields: Array<{ label: string; value: string }> = [];
  if (externalRef?.companyRequestId)
    refFields.push({ label: 'Company request', value: externalRef.companyRequestId });
  if (externalRef?.sortedCandidateId)
    refFields.push({ label: 'Candidate', value: externalRef.sortedCandidateId });
  if (externalRef?.interviewId)
    refFields.push({ label: 'Interview', value: externalRef.interviewId });

  return (
    <div
      className={cn(
        'mx-auto w-full max-w-5xl p-4 transition-opacity sm:p-6 lg:p-8',
        loading && 'opacity-60',
      )}
    >
      <BackLink />

      <header className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-balance">{meeting.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <MeetingStatusBadge status={meeting.status} />
            <JoinLinkStatusBadge state={joinWindowState(meeting)} />
            <RecordingStatusBadge state={meeting.recordingState} />
            <code className="text-muted-foreground border-border/70 rounded-md border bg-white/[0.03] px-2 py-1 font-mono text-xs">
              {meeting.code}
            </code>
          </div>
        </div>

        <CancelMeetingAction meeting={meeting} onDone={reload} />
      </header>

      <div className="mt-6 space-y-4">
        <Section title="Summary" icon={CalendarClock}>
          <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Scheduled">{formatDateTime(meeting.startsAt)}</Field>
            <Field label="Duration">{formatMinutes(meeting.durationMin)}</Field>
            <Field label="Join window">{formatWindow(meeting.joinOpensAt, meeting.joinClosesAt)}</Field>
            <Field label="Link status">
              <JoinWindowSummary input={meeting} showBadge={false} />
            </Field>
            <Field label="Recording mode">
              <RecordingModeLabel mode={meeting.recording} />
            </Field>
            <Field label="Created">{formatDateTime(meeting.createdAt)}</Field>
            <Field label="Started">{formatDateTime(meeting.startedAt)}</Field>
            <Field label="Ended">{formatDateTime(meeting.endedAt)}</Field>
            <Field label="Together">
              {meeting.overlapMs === null ? (
                <span title="A join or leave is missing, so the time they spent together is not known.">
                  Cannot be calculated
                </span>
              ) : (
                formatDurationMs(meeting.overlapMs)
              )}
            </Field>
            {refFields.map((field) => (
              <Field key={field.label} label={field.label}>
                <span className="font-mono text-xs break-all">{field.value}</span>
              </Field>
            ))}
          </dl>
        </Section>

        <Section title="Participants" icon={Users}>
          {meeting.participants.length === 0 ? (
            <EmptyRow icon={Users} message="Nobody joined this meeting." />
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2">
              {meeting.participants.map((participant) => (
                <ParticipantRow key={participant.identity} participant={participant} />
              ))}
            </ul>
          )}
        </Section>

        <Section title="Recordings" icon={Film}>
          {recordings.length === 0 ? (
            <EmptyRow
              icon={VideoOff}
              message={
                meeting.recording === 'off'
                  ? 'Recording was switched off for this interview.'
                  : 'No recording was produced for this interview.'
              }
            />
          ) : (
            <ul className="space-y-4">
              {recordings.map((recording) => (
                <RecordingRow
                  key={recording.recordingId}
                  recording={recording}
                  title={meeting.title}
                />
              ))}
            </ul>
          )}
        </Section>

        {id && <JoinLinksSection meetingId={id} meeting={meeting} />}

        <Section title="Chat" icon={MessageSquare}>
          {messages.length === 0 ? (
            <EmptyRow
              icon={MessageSquare}
              message="No in-call messages were saved for this meeting."
            />
          ) : (
            <ul className="space-y-3">
              {messages.map((message) => (
                <ChatRow key={message.id} message={message} />
              ))}
            </ul>
          )}
        </Section>

        <Section title="Timeline" icon={Radio}>
          {timeline.length === 0 ? (
            <EmptyRow icon={Inbox} message="Nothing has happened on this meeting yet." />
          ) : (
            <ol className="space-y-4">
              {timeline.map((event, index) => (
                <TimelineRow
                  key={`${event.type}-${event.at}-${index}`}
                  event={event}
                  last={index === timeline.length - 1}
                />
              ))}
            </ol>
          )}
        </Section>
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/admin/meetings"
      className="text-muted-foreground hover:text-foreground inline-flex min-h-11 items-center gap-2 rounded-lg text-sm transition-colors"
    >
      <ArrowLeft className="size-4" aria-hidden />
      All meetings
    </Link>
  );
}

function Section({
  title,
  icon: Icon,
  children,
  actions,
}: {
  title: string;
  icon: LucideIcon;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Card className="p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-muted-foreground flex items-center gap-2 text-[11px] font-semibold tracking-[0.09em] uppercase">
          <Icon className="size-3.5" aria-hidden />
          {title}
        </h2>
        {actions}
      </div>
      <div className="mt-4">{children}</div>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
        {label}
      </dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

function EmptyRow({ icon: Icon, message }: { icon: LucideIcon; message: string }) {
  return (
    <p className="text-muted-foreground flex items-center gap-2.5 rounded-xl border border-dashed px-4 py-6 text-sm text-pretty">
      <Icon className="size-4 shrink-0" aria-hidden />
      {message}
    </p>
  );
}

function ChatRow({ message }: { message: AdminChatMessage }) {
  const host = message.role === 'host';
  return (
    <li className={cn('flex', host ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-xl border px-3 py-2',
          host
            ? 'border-primary/25 bg-primary/12'
            : 'border-border/60 bg-white/[0.03]',
        )}
      >
        <div className="text-muted-foreground flex items-baseline gap-2 text-[11px]">
          <span className="font-medium text-foreground">{message.name || (host ? 'Host' : 'Guest')}</span>
          <span className="capitalize">{message.role}</span>
          <time dateTime={message.at}>{formatDateTime(message.at)}</time>
        </div>
        <p className="mt-1 text-sm break-words whitespace-pre-wrap">{message.text}</p>
      </div>
    </li>
  );
}

function ParticipantRow({ participant }: { participant: MeetingParticipantView }) {
  return (
    <li className="border-border/60 rounded-xl border bg-white/[0.03] p-4">
      <div className="flex items-start gap-3">
        <Avatar className="size-9 shrink-0 ring-1 ring-white/10">
          <AvatarFallback>{initials(participant.name)}</AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-medium">{participant.name}</p>
            <Badge variant={ROLE_VARIANT[participant.role]} className="shrink-0 capitalize">
              {participant.role}
            </Badge>
          </div>

          {participant.present && (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-[var(--success)]">
              <span className="animate-pulse-dot size-1.5 rounded-full bg-current" aria-hidden />
              still in the call
            </p>
          )}

          <dl className="text-muted-foreground mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
            <div>
              <dt className="inline">Joined </dt>
              <dd className="text-foreground inline">{formatDateTime(participant.joinedAt)}</dd>
            </div>
            <div>
              <dt className="inline">Left </dt>
              <dd className="text-foreground inline">
                {participant.present ? EM_DASH : formatDateTime(participant.leftAt)}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="inline">Connected for </dt>
              <dd className="text-foreground inline tabular-nums">
                {formatDurationMs(participant.durationMs)}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </li>
  );
}

function RecordingRow({
  recording,
  title,
}: {
  recording: MeetingRecordingView;
  title: string;
}) {
  const playable = recording.status === 'completed' && Boolean(recording.url);

  return (
    <li className="border-border/60 rounded-xl border bg-white/[0.03] p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <RecordingStatusBadge state={recording.status} />
        <code className="text-muted-foreground font-mono text-xs break-all">
          {recording.recordingId}
        </code>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        <Field label="Started">{formatDateTime(recording.startedAt)}</Field>
        <Field label="Ended">{formatDateTime(recording.endedAt)}</Field>
        <Field label="Length">{formatDurationMs(recording.durationMs)}</Field>
        <Field label="Size">{formatBytes(recording.sizeBytes)}</Field>
      </dl>

      {recording.status === 'failed' && (
        <p className="mt-3 text-xs text-[color-mix(in_oklab,var(--destructive)_80%,white)] text-pretty">
          This recording failed, so there is no video for this interview.
        </p>
      )}

      {playable && recording.url && (
        <div className="mt-4">
          <video
            controls
            playsInline
            preload="metadata"
            src={recording.url}
            title={`Recording of ${title}`}
            className="border-border/60 aspect-video w-full rounded-xl border bg-black"
          />

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button variant="outline" size="sm" asChild>
              <a href={recording.url} download rel="noreferrer">
                <Download className="size-4" />
                Download
              </a>
            </Button>
            {recording.urlExpiresAt && (
              <p className="text-muted-foreground text-xs text-pretty">
                This playback and download link expires {formatDateTime(recording.urlExpiresAt)}.
                Reload the page to get a fresh one.
              </p>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

function TimelineRow({ event, last }: { event: MeetingEventView; last: boolean }) {
  const { icon: Icon, tone } = EVENT_META[event.type];
  const reason = detailString(event.detail, 'reason');

  return (
    <li className="relative flex gap-3.5">
      {!last && (
        <span aria-hidden className="bg-border absolute top-9 bottom-[-1rem] left-[1.125rem] w-px" />
      )}

      <span
        className={cn('grid size-9 shrink-0 place-items-center rounded-full border', TONE_RING[tone])}
      >
        <Icon className="size-4" aria-hidden />
      </span>

      <div className="min-w-0 flex-1 pt-1">
        <p className="text-sm font-medium text-pretty">{eventSentence(event)}</p>
        <p className="text-muted-foreground mt-0.5 text-xs">
          {formatDateTime(event.at)}
          {event.role && <span className="capitalize"> · {event.role}</span>}
        </p>
        {reason && (
          <p className="text-muted-foreground mt-1 text-xs text-pretty">Reason: {reason}</p>
        )}
      </div>
    </li>
  );
}

/**
 * Join links are call credentials, not metadata: anyone holding one can enter
 * the room as that person. They are fetched only when an admin deliberately
 * asks, so a shoulder-surfed console or a screenshot of this page does not leak
 * access to every interview.
 */
function JoinLinksSection({
  meetingId,
  meeting,
}: {
  meetingId: string;
  meeting: MeetingView;
}) {
  const [links, setLinks] = useState<AdminMeetingLinksResponse | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const copyTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (copyTimer.current !== null) window.clearTimeout(copyTimer.current);
    },
    [],
  );

  const reveal = async () => {
    setPending(true);
    setError(null);
    try {
      setLinks(
        await withTimeout(
          adminMeetingLinks(meetingId),
          REQUEST_TIMEOUT_MS,
          'The server took too long to respond.',
        ),
      );
    } catch (err) {
      setError(errorMessage(err, 'Could not fetch the join links.'));
    } finally {
      setPending(false);
    }
  };

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      if (copyTimer.current !== null) window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopied(null), 2000);
    } catch {
      setError('Could not copy automatically. Select the link and copy it manually.');
    }
  };

  return (
    <Section title="Join links" icon={KeyRound}>
      <JoinWindowSummary input={meeting} className="mb-4" />

      <p className="text-muted-foreground flex items-start gap-2 text-sm text-pretty">
        <ShieldAlert className="mt-0.5 size-4 shrink-0 text-[var(--warning)]" aria-hidden />
        Anyone with one of these links can join this interview as that person. Share them only with
        the host or the candidate they belong to. The two links share one window: when it closes,
        both stop working.
      </p>

      {error && (
        <p role="alert" className="mt-3 text-sm text-[color-mix(in_oklab,var(--destructive)_80%,white)]">
          {error}
        </p>
      )}

      {!links ? (
        <Button variant="outline" className="mt-4" onClick={reveal} disabled={pending}>
          {pending ? <Spinner className="size-4" /> : <Eye className="size-4" />}
          {pending ? 'Fetching…' : 'Reveal join links'}
        </Button>
      ) : (
        <div className="mt-4 space-y-3">
          <JoinLinkRow
            label="Host"
            url={links.hostUrl}
            copied={copied === 'Host'}
            onCopy={() => copy('Host', links.hostUrl)}
          />
          <JoinLinkRow
            label="Guest"
            url={links.guestUrl}
            copied={copied === 'Guest'}
            onCopy={() => copy('Guest', links.guestUrl)}
          />
        </div>
      )}
    </Section>
  );
}

function JoinLinkRow({
  label,
  url,
  copied,
  onCopy,
}: {
  label: string;
  url: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="border-border/60 rounded-xl border bg-white/[0.03] p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.08em] uppercase">
          <Link2 className="size-3" aria-hidden />
          {label}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCopy}
          aria-label={`Copy ${label.toLowerCase()} join link`}
        >
          {copied ? (
            <Check className="size-4 text-[var(--success)]" />
          ) : (
            <Copy className="size-4" />
          )}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="mt-1.5 font-mono text-xs break-all select-all">{url}</p>
    </div>
  );
}

function CancelMeetingAction({
  meeting,
  onDone,
}: {
  meeting: MeetingView;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (meeting.status !== 'scheduled' && meeting.status !== 'live') return null;

  const live = meeting.status === 'live';
  const actionLabel = live ? 'End meeting' : 'Cancel meeting';

  const confirm = async () => {
    setPending(true);
    setError(null);
    try {
      await withTimeout(
        adminCancelMeeting(meeting.meetingId, reason.trim() || undefined),
        REQUEST_TIMEOUT_MS,
        'The server took too long to respond.',
      );
      setOpen(false);
      setReason('');
      onDone();
    } catch (err) {
      setError(errorMessage(err, `Could not ${actionLabel.toLowerCase()}.`));
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <Button
        variant="destructive"
        className="w-full lg:w-auto"
        onClick={() => setOpen(true)}
      >
        {live ? <PhoneOff className="size-4" /> : <Ban className="size-4" />}
        {actionLabel}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{live ? 'End this interview now?' : 'Cancel this interview?'}</DialogTitle>
            <DialogDescription>
              {live
                ? 'Everyone is disconnected immediately and the recording is finalised. This cannot be undone.'
                : 'The join links stop working and nobody will be able to enter the room. This cannot be undone.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="cancel-reason">Reason (optional)</Label>
            <Input
              id="cancel-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Candidate rescheduled"
              autoComplete="off"
              disabled={pending}
            />
            <p className="text-muted-foreground text-xs text-pretty">
              Stored on the meeting timeline so the next person to look knows why.
            </p>
          </div>

          {error && (
            <p
              role="alert"
              className="text-sm text-[color-mix(in_oklab,var(--destructive)_80%,white)]"
            >
              {error}
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={pending}>
              Keep it
            </Button>
            <Button variant="destructive" onClick={confirm} disabled={pending}>
              {pending && <Spinner className="size-4" />}
              {actionLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DetailSkeleton() {
  return (
    <div className="mx-auto w-full max-w-5xl p-4 sm:p-6 lg:p-8" aria-busy="true" aria-label="Loading meeting">
      <Skeleton className="h-4 w-28" />

      <div className="mt-6 space-y-3">
        <Skeleton className="h-7 w-2/3 max-w-md" />
        <div className="flex gap-2">
          <Skeleton className="h-6 w-24 rounded-full" />
          <Skeleton className="h-6 w-28 rounded-full" />
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {[0, 1, 2].map((section) => (
          <Card key={section} className="p-5 sm:p-6">
            <Skeleton className="h-3 w-28" />
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((field) => (
                <div key={field} className="space-y-2">
                  <Skeleton className="h-2.5 w-20" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
